import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    // Header
    nav_works: 'Works',
    nav_sculptures: 'White Dolls',
    nav_about: 'About',
    nav_contact: 'Contact',
    // Hero
    hero_subtitle: 'Contemporary Artist',
    hero_tagline: 'Beauty hidden inside chaos.',
    hero_cta_primary: 'Available Work',
    hero_cta_secondary: 'About Artist',
    shipping_banner: '🇲🇽 Free shipping in Mexico · + Make an offer',
    // Works
    works_title: 'Works',
    works_description: 'A collection of contemporary pieces exploring the dialogue between chaos and form',
    // Sculptures
    sculptures_title: 'White Dolls',
    sculptures_description: 'Three-dimensional explorations of form, space, and materiality',
    // Badges
    badge_available: 'Available',
    badge_sold: 'Sold',
    view_details: 'View Details',
    // Modal
    add_to_cart: 'Add to Cart',
    make_offer: 'Make an Offer',
    frame_choose: 'Choose frame',
    frame_black: 'Black',
    frame_silver: 'Silver',
    frame_gold: 'Gold',
    detail_shipping: 'FREE SHIPPING IN MEXICO',
    detail_ready: 'Ready to hang',
    detail_signed: 'Signed and dated on the back',
    detail_certificate: 'Includes signed certificate of authenticity',
    you_may_like: 'You may also like',
    shipping_returns: 'Shipping & Returns',
    favorites_title: 'Favorites',
    no_favorites: 'No favorites yet. Tap the heart on artworks you love.',
    // About
    about_title: 'About',
    // Contact
    contact_title: 'Contact',
    contact_description: 'For inquiries, commissions, or gallery representation',
    contact_name: 'Name',
    contact_email: 'Email',
    contact_message: 'Message',
    contact_send: 'Send Message',
    // Cart
    cart_title: 'Shopping Cart',
    cart_empty: 'Your cart is empty',
    cart_total: 'Total:',
    cart_checkout: 'Proceed to Checkout',
    cart_clear: 'Clear Cart',
    cart_processing: 'Processing...',
    // Payment methods
    pay_choose_method: 'Choose payment method',
    pay_card: 'Credit / Debit Card',
    pay_card_desc: 'Visa, Mastercard, Amex · Apple Pay & Google Pay on supported devices',
    pay_paypal: 'PayPal',
    pay_paypal_desc: 'Pay securely with your PayPal account',
    pay_transfer: 'Direct Bank Transfer',
    pay_transfer_desc: 'Receive bank details and confirmation by email',
    pay_continue: 'Continue',
    pay_transfer_form_title: 'Shipping & contact details',
    pay_transfer_form_help: 'We will email you the bank details and reference so you can complete the transfer.',
    form_name: 'Full name',
    form_email: 'Email',
    form_phone: 'Phone (optional)',
    form_notes: 'Notes (optional)',
    submit_request: 'Submit order',
    ship_country: 'Country',
    ship_address: 'Street + number',
    ship_address2: 'Apt / Suite / Reference (optional)',
    ship_city: 'City',
    ship_state: 'State / Province',
    ship_postal: 'ZIP / Postal code',
    ship_to: 'Ship to',
    ship_subtotal: 'Subtotal',
    ship_shipping: 'Shipping',
    ship_free: 'Free',
    ship_quote: 'Quoted upon request',
    ship_quote_help: 'International shipping is quoted personally based on the destination and piece. We will contact you within 24 hrs with the final amount.',
    ship_total: 'Total',
    ship_total_partial: 'Total (without shipping)',
    // Footer
    footer_tagline: 'Contemporary Artist',
    footer_rights: 'All rights reserved.',
    // Image labels
    label_full_artwork: 'Full Artwork',
    label_detail: 'Detail',
    label_in_scene: 'In Scene',
    label_full_sculpture: 'Full Sculpture',
    // Share
    share_title: 'Share',
    share_copy: 'Copy link',
    share_copied: 'Link copied!',
    share_pinterest: 'Pin it',
    share_whatsapp: 'WhatsApp',
    back_to_home: 'Back to home',
    loading_artwork: 'Loading artwork…',
    artwork_not_found: 'Artwork not found.',
    // Catalog banner + Ask on WhatsApp
    catalog_banner_title: 'Download the full catalogue',
    catalog_banner_sub: 'Available pieces, dimensions and prices',
    catalog_banner_cta: 'Request catalogue',
    catalog_wa_message: 'Hi, I would like the full catalogue of available pieces with prices.',
    ask_whatsapp: 'Ask on WhatsApp',
    ask_whatsapp_message: 'Hi, I am interested in the piece "{title}". Could you send me more information?',
  },
  es: {
    // Header
    nav_works: 'Obras',
    nav_sculptures: 'White Dolls',
    nav_about: 'Sobre',
    nav_contact: 'Contacto',
    // Hero
    hero_subtitle: 'Artista Contemporánea',
    hero_tagline: 'La belleza escondida en el caos.',
    hero_cta_primary: 'Obra Disponible',
    hero_cta_secondary: 'Sobre la Artista',
    shipping_banner: '🇲🇽 Envío gratis en México · + Haz una oferta',
    // Works
    works_title: 'Obras',
    works_description: 'Una colección de piezas contemporáneas que exploran el diálogo entre el caos y la forma',
    // Sculptures
    sculptures_title: 'White Dolls',
    sculptures_description: 'Exploraciones tridimensionales de la forma, el espacio y la materialidad',
    // Badges
    badge_available: 'Disponible',
    badge_sold: 'Vendida',
    view_details: 'Ver Detalles',
    // Modal
    add_to_cart: 'Añadir al Carrito',
    make_offer: 'Hacer una Oferta',
    frame_choose: 'Elige el marco',
    frame_black: 'Negro',
    frame_silver: 'Plateado',
    frame_gold: 'Dorado',
    detail_shipping: 'ENVÍO GRATIS EN MÉXICO',
    detail_ready: 'Lista para colgar',
    detail_signed: 'Firmada y fechada al reverso',
    detail_certificate: 'Incluye certificado de autenticidad firmado',
    you_may_like: 'Te puede gustar',
    shipping_returns: 'Envíos y Devoluciones',
    favorites_title: 'Favoritos',
    no_favorites: 'Aún no hay favoritos. Da tap al corazón en las obras que te gusten.',
    // About
    about_title: 'Sobre',
    // Contact
    contact_title: 'Contacto',
    contact_description: 'Para consultas, comisiones o representación en galerías',
    contact_name: 'Nombre',
    contact_email: 'Correo',
    contact_message: 'Mensaje',
    contact_send: 'Enviar Mensaje',
    // Cart
    cart_title: 'Carrito',
    cart_empty: 'Tu carrito está vacío',
    cart_total: 'Total:',
    cart_checkout: 'Proceder al Pago',
    cart_clear: 'Vaciar Carrito',
    cart_processing: 'Procesando...',
    // Payment methods
    pay_choose_method: 'Elige tu método de pago',
    pay_card: 'Tarjeta de Crédito / Débito',
    pay_card_desc: 'Visa, Mastercard, Amex · Apple Pay y Google Pay en dispositivos compatibles',
    pay_paypal: 'PayPal',
    pay_paypal_desc: 'Paga de forma segura con tu cuenta de PayPal',
    pay_transfer: 'Transferencia Bancaria Directa',
    pay_transfer_desc: 'Recibe los datos bancarios y la confirmación por correo',
    pay_continue: 'Continuar',
    pay_transfer_form_title: 'Datos de envío y contacto',
    pay_transfer_form_help: 'Te enviaremos los datos bancarios y la referencia por correo para completar la transferencia.',
    form_name: 'Nombre completo',
    form_email: 'Correo electrónico',
    form_phone: 'Teléfono (opcional)',
    form_notes: 'Notas (opcional)',
    submit_request: 'Enviar pedido',
    ship_country: 'País',
    ship_address: 'Calle y número',
    ship_address2: 'Depto / Interior / Referencia (opcional)',
    ship_city: 'Ciudad',
    ship_state: 'Estado',
    ship_postal: 'Código postal',
    ship_to: 'Enviar a',
    ship_subtotal: 'Subtotal',
    ship_shipping: 'Envío',
    ship_free: 'Gratis',
    ship_quote: 'Se cotiza',
    ship_quote_help: 'El envío internacional se cotiza de forma personalizada según destino y pieza. Te contactaremos en menos de 24 hrs con el monto final.',
    ship_total: 'Total',
    ship_total_partial: 'Total (sin envío)',
    // Footer
    footer_tagline: 'Artista Contemporánea',
    footer_rights: 'Todos los derechos reservados.',
    // Image labels
    label_full_artwork: 'Full Artwork',
    label_detail: 'Detail',
    label_in_scene: 'In Scene',
    label_full_sculpture: 'Full Sculpture',
    // Share
    share_title: 'Compartir',
    share_copy: 'Copiar enlace',
    share_copied: '¡Enlace copiado!',
    share_pinterest: 'Guardar en Pinterest',
    share_whatsapp: 'WhatsApp',
    back_to_home: 'Volver al inicio',
    loading_artwork: 'Cargando obra…',
    artwork_not_found: 'Obra no encontrada.',
    // Catalog banner + Ask on WhatsApp
    catalog_banner_title: 'Descarga el catálogo completo',
    catalog_banner_sub: 'Piezas disponibles, medidas y precios',
    catalog_banner_cta: 'Solicitar catálogo',
    catalog_wa_message: 'Hola, me interesa el catálogo completo de piezas disponibles con precios.',
    ask_whatsapp: 'Preguntar por WhatsApp',
    ask_whatsapp_message: 'Hola, me interesa la pieza "{title}". ¿Me puedes dar más información?',
  }
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem('lang');
    if (saved && translations[saved]) return saved;
    // Detect browser language
    const browserLang = navigator.language?.toLowerCase().slice(0, 2);
    return browserLang === 'es' ? 'es' : 'en';
  });

  useEffect(() => {
    localStorage.setItem('lang', lang);
  }, [lang]);

  const t = (key) => translations[lang]?.[key] || translations.en[key] || key;

  const toggleLang = () => {
    setLang(lang === 'en' ? 'es' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
